import { companyName, IS_MAIN_COMPANY } from 'config'
import { removeNonPagePermissions } from 'hooks/hasPagePermissions'
import { usePermissions } from 'hooks/usePermissions'
import { useEffect, useMemo, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useTitle } from 'utils/pageTitle'

import { AccountExecutives } from './AccountExecutives'
import { BankerSearch } from './BankerSearch'
import { BorrowerPipelineConfiguration } from './BorrowerPipelineConfiguration'
import { AccountVerificationType, BrokerVerificationCategory } from './BrokerVerificationCategory'
import { CompanyBroadcast } from './CompanyBroadcast'
import { Configuration } from './Configuration/index'
import { Credentials } from './Credentials'
import { EmailDNSSetup } from './EmailDNSSetup'
import { EmailLogs } from './EmailLogs'
import { ExpirationDateType } from './ExpirationDateType'
import { HomePageCreditBox } from './HomePageCreditBox'
import { LandingLearnMore } from './LandingLearnMore'
import { LoginPageNotifications } from './LoginPageNotifications'
import { ManageUtmAnalysis } from './ManageUtmAnalysis'
import { ManageUtmLogs } from './ManageUtmLogs'
import { NewPermissions } from './NewPermissions'
import { PagePermissions } from './PagePermissions'
import { Projects } from './Projects'
import { ResourceBroadcast } from './ResourceBroadcast'
import { StripeManagements } from './StripeManage'
import { Survey } from './Survey'

type menuType = {
  [key: string]: string //fix this
}

export default function AdminTools() {
  useTitle(`Admin Tools - ${companyName}`)

  const { menu = '' } = useParams() as any
  const navigate = useHistory()
  const [selectedMenu, setSelectedMenu] = useState(menu)
  const [menus, setMenus] = useState<menuType>({})
  const [changed, setChanged] = useState(false)

  const { hasPermission } = usePermissions()

  useEffect(() => {
    let temp: menuType = {}
    if (hasPermission('MANAGE_ADMIN_TOOLS')) {
      temp = {
        permissions: 'Permissions',
        pagePermissions: 'Page Permissions',
        homePage: 'Home Page Credit Box',
        loginModal: 'Login Page Notifications',
        companyBroadcast: 'Company Broadcast',
        brokerVerification: 'Broker Approval Categories',
        correspondentVerification: 'Correspondent Approval Categories',
        tablefunderVerification: 'Table Funder Approval Categories',
        contractorApprovalVerification: 'Contractor Approval Categories',
        resourcesBroadcast: 'Resources Broadcast',
        stripeManage: 'Stripe Management',
        expirationDatesTypes: 'Expiration Date',
        configuration: 'Configuration',
        emailDNSSetup: 'Email DNS Setup',
        emailLogs: 'Email Logs',
        projects: 'Projects',
        credentials: 'AMC Credentials',
        borrowerPipeline: 'Customize Borrower Application',
        brokerPipeline: 'Customize Broker Application',
        utmAnalysis: 'UTM Analysis',
        utmLogs: 'UTM Logs',
      }
      if (IS_MAIN_COMPANY) delete temp.pagePermissions
    }
    if (hasPermission('MANAGE_APPRAISER_SETTLEMENT_CONTRACTOR')) {
      temp = {
        ...temp,
        bankerSearch: 'Appraiser / Settlement Agent / Contractor / Guarantor',
      }
    }
    if (hasPermission('MANAGE_REVIEWS')) {
      temp = {
        ...temp,
        reviews: 'Reviews',
        accountExecutives: 'Account Executives',
        landingLearnMore: 'Landing Learn More',
      }
    }

    removeNonPagePermissions(temp, ['admin_tool'])
    setMenus(temp)
  }, [])

  const setMenu = (menuItem: string, changed?: boolean) => {
    setSelectedMenu(menuItem)
    if (!changed) navigate.push(`/admin_tool/${menuItem}`)
  }

  const renderFragment = useMemo(() => {
    switch (selectedMenu) {
      case 'bankerSearch':
        return <BankerSearch />

      case 'permissions':
        return <NewPermissions />

      case 'pagePermissions':
        return <PagePermissions />

      case 'homePage':
        return <HomePageCreditBox />

      case 'loginModal':
        return <LoginPageNotifications />

      case 'companyBroadcast':
        return <CompanyBroadcast />

      case 'brokerVerification':
      case 'correspondentVerification':
      case 'tablefunderVerification':
      case 'contractorApprovalVerification':
        return (
          <div key={selectedMenu}>
            <BrokerVerificationCategory type={selectedMenu.replace('Verification', '') as AccountVerificationType} />
          </div>
        )

      case 'resourcesBroadcast':
        return <ResourceBroadcast />

      case 'stripeManage':
        return <StripeManagements />

      case 'expirationDatesTypes':
        return <ExpirationDateType />

      case 'reviews':
        return <Survey />

      case 'accountExecutives':
        return <AccountExecutives />

      case 'landingLearnMore':
        return <LandingLearnMore />

      case 'configuration':
        return <Configuration setChanged={setChanged} />

      case 'emailDNSSetup':
        return <EmailDNSSetup />

      case 'emailLogs':
        return <EmailLogs />

      case 'projects':
        return <Projects />

      case 'credentials':
        return <Credentials />

      case 'borrowerPipeline':
        return <BorrowerPipelineConfiguration key="borrower" type="borrower" setChanged={setChanged} />

      case 'brokerPipeline':
        return <BorrowerPipelineConfiguration key="broker" type="broker" setChanged={setChanged} />

      case 'utmAnalysis':
        return <ManageUtmAnalysis />

      case 'utmLogs':
        return <ManageUtmLogs />

      default: {
        return <div>Comming Soon...</div>
      }
    }
  }, [selectedMenu])

  useEffect(() => {
    let newMenu = menu
    if (!menu || !Object.keys(menus).includes(newMenu)) newMenu = Object.keys(menus)[0]
    if (!newMenu) return
    setMenu(newMenu)
  }, [menus, menu])

  const onChangeMenu = async (value: string) => {
    if (changed) {
      const isContinue = await confirm(`You've made some changes!\nAre you sure want to leave without Saving?`)
      if (!isContinue) return
    }
    setMenu(value, changed)
    setChanged(false)
  }

  return (
    <div className="admin-tools-container py-6">
      <div className="admin-tools-wrapper max-w-screen-2xl m-auto grid grid-cols-12 gap-6 px-2">
        <div className="sidebar-left col-span-12 md:col-span-3 shrink-0 bg-white shadow1 rounded">
          <ul className="sidebar-items flex flex-col p-4 pb-20">
            {Object.keys(menus).map((item: string, index) => {
              return (
                <li
                  key={index}
                  onClick={() => {
                    onChangeMenu(item)
                  }}
                  className="border-b py-2"
                >
                  <p
                    className={`hover:underline cursor-pointer ${
                      selectedMenu === item ? 'border px-4 py-1 bg-zinc-100' : 'py-1'
                    }`}
                  >
                    {index + 1}. {menus[item as keyof typeof menus]}
                  </p>
                </li>
              )
            })}
          </ul>
        </div>
        <div className="content-right col-span-12 md:col-span-9 bg-white p-4 rounded shadow1">{renderFragment}</div>
      </div>
    </div>
  )
}
